
import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuLink,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

interface HeaderProps {
  navStyle?: 'light' | 'default';
}

const Header: React.FC<HeaderProps> = ({ navStyle = 'default' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const effectiveScrolled = navStyle === 'light' || isScrolled;

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'auto';
  }, [location]);

  const services = [
    { name: 'Website Design', path: '/services/website-design' },
    { name: 'Photography', path: '/services/photography' },
    { name: 'Marketing', path: '/services/marketing' },
    { name: 'SEO Services', path: '/services/seo' },
    { name: 'Branding & Identity', path: '/services/branding' },
    { name: 'Software', path: '/services/software' },
    { name: 'Real Estate Media', path: '/services/real-estate-media' },
    { name: 'Tech Coaching', path: '/services/tech-coaching' },
    { name: 'Virtual Tours', path: '/services/virtual-tours' },
    { name: 'LED Printing', path: '/services/led-printing' },
  ];

  return (
    <header 
      className={`w-full z-50 fixed top-0 transition-all duration-300 ${
        effectiveScrolled 
          ? 'py-3 bg-white/90 backdrop-blur-md shadow-sm navbar-fixed' 
          : 'py-5 bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            {effectiveScrolled ? (
              <img 
                src="/lovable-uploads/d023512a-50be-4738-9760-d5afcf453819.png" 
                alt="Astrem" 
                className="h-10 w-auto" 
              />
            ) : (
              <img 
                src="/lovable-uploads/037dfed3-3f72-4a84-b00f-e0450a978e13.png" 
                alt="Astrem" 
                className="h-10 w-auto" 
              />
            )}
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`${effectiveScrolled ? 'text-astrem-gray-800 hover:text-astrem-gray-600' : 'text-white hover:text-gray-300'} transition-colors font-medium text-sm ${location.pathname === '/' ? effectiveScrolled ? 'text-black' : 'text-white' : ''}`}
            >
              Home
            </Link>
            
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={`${effectiveScrolled ? 'text-astrem-gray-800 hover:text-astrem-gray-600' : 'text-white hover:text-gray-300'} transition-colors font-medium text-sm bg-transparent ${services.some(service => location.pathname === service.path) ? effectiveScrolled ? 'text-black' : 'text-white' : ''}`}>
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                      {services.map((service) => (
                        <li key={service.name}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={service.path}
                              className={cn(
                                "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                                location.pathname === service.path ? "bg-accent" : ""
                              )}
                            >
                              <div className="text-sm font-medium leading-none">{service.name}</div>
                              <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                                {getServiceDescription(service.name)}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            <Link
              to="/portfolio"
              className={`${effectiveScrolled ? 'text-astrem-gray-800 hover:text-astrem-gray-600' : 'text-white hover:text-gray-300'} transition-colors font-medium text-sm ${location.pathname === '/portfolio' ? effectiveScrolled ? 'text-black' : 'text-white' : ''}`}
            >
              Portfolio
            </Link>
            
            <Link
              to="/blog"
              className={`${effectiveScrolled ? 'text-astrem-gray-800 hover:text-astrem-gray-600' : 'text-white hover:text-gray-300'} transition-colors font-medium text-sm ${location.pathname.startsWith('/blog') ? effectiveScrolled ? 'text-black' : 'text-white' : ''}`}
            >
              Blog
            </Link>
            
            <Link
              to="/about"
              className={`${effectiveScrolled ? 'text-astrem-gray-800 hover:text-astrem-gray-600' : 'text-white hover:text-gray-300'} transition-colors font-medium text-sm ${location.pathname === '/about' ? effectiveScrolled ? 'text-black' : 'text-white' : ''}`}
            >
              About
            </Link>
            
            <Link
              to="/contact"
              className={`${effectiveScrolled ? 'text-astrem-gray-800 hover:text-astrem-gray-600' : 'text-white hover:text-gray-300'} transition-colors font-medium text-sm ${location.pathname === '/contact' ? effectiveScrolled ? 'text-black' : 'text-white' : ''}`}
            >
              Contact
            </Link>
          </nav>

          <div className="hidden md:block">
            <Button asChild className={`${effectiveScrolled ? 'bg-astrem-black hover:bg-astrem-gray-800' : 'bg-white text-astrem-black hover:bg-gray-200'} rounded-full px-6`}>
              <Link to="/contact">Get Started</Link>
            </Button>
          </div>

          <button
            className={`md:hidden ${effectiveScrolled ? 'text-astrem-gray-800' : 'text-white'}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="fixed inset-0 z-50 bg-white md:hidden pt-20 px-6 overflow-y-auto">
          <div className="flex justify-between items-center absolute top-6 left-4 right-4">
            <Link to="/" onClick={() => setIsMenuOpen(false)}>
              <img 
                src="/lovable-uploads/d023512a-50be-4738-9760-d5afcf453819.png" 
                alt="Astrem" 
                className="h-8 w-auto" 
              />
            </Link>
            <button 
              onClick={toggleMenu} 
              className="text-astrem-gray-800 p-2"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <nav className="flex flex-col space-y-6 py-6 text-center mt-6">
            <Link
              to="/"
              onClick={toggleMenu}
              className="text-xl font-medium text-astrem-gray-800 py-2"
            >
              Home
            </Link>
            
            <div className="flex flex-col space-y-4">
              <div className="text-xl font-medium text-astrem-gray-800 py-2">Services</div>
              <div className="flex flex-col space-y-4 px-4">
                {services.map((service) => (
                  <Link
                    key={service.name}
                    to={service.path}
                    onClick={toggleMenu}
                    className="text-base font-medium text-astrem-gray-600 py-1"
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            </div>
            
            <Link
              to="/portfolio"
              onClick={toggleMenu}
              className="text-xl font-medium text-astrem-gray-800 py-2"
            >
              Portfolio
            </Link>
            
            <Link
              to="/blog"
              onClick={toggleMenu}
              className="text-xl font-medium text-astrem-gray-800 py-2"
            >
              Blog
            </Link>
            
            <Link
              to="/about"
              onClick={toggleMenu}
              className="text-xl font-medium text-astrem-gray-800 py-2"
            >
              About
            </Link>
            
            <Link
              to="/contact"
              onClick={toggleMenu}
              className="text-xl font-medium text-astrem-gray-800 py-2"
            >
              Contact
            </Link>
            
            <Button asChild className="mt-6 bg-astrem-black text-white rounded-full">
              <Link to="/contact" onClick={toggleMenu}>
                Get Started
              </Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

const getServiceDescription = (serviceName: string) => {
  switch (serviceName) {
    case 'Website Design':
      return 'Custom websites designed for conversion and user experience, built with modern technologies.';
    case 'Photography':
      return 'Professional photography services for real estate, fashion, food, events, and more.';
    case 'Marketing':
      return 'Comprehensive marketing strategies including social media management and content creation.';
    case 'SEO Services':
      return 'Improve your website visibility in search engines and drive organic traffic to your business.';
    case 'Branding & Identity':
      return 'Brand development including logo design, messaging, and creating a cohesive identity.';
    case 'IT Services':
      return 'Comprehensive IT support, network management, cybersecurity, and technology consulting.';
    case 'Tech Consulting':
      return 'Strategic technology guidance to help businesses make informed decisions and implement solutions.';
    case 'App Development':
      return 'Custom mobile and web application development for various platforms and business needs.';
    case 'Real Estate Media':
      return 'Professional photography, virtual tours, and media services for real estate professionals.';
    case 'Software':
      return 'Custom software solutions designed to streamline operations and enhance business efficiency.';
    case 'Tech Coaching':
      return 'Personalized coaching for podcast creation, AI implementation, and digital transformation.';
    case 'Virtual Tours':
      return 'Custom 360° immersive virtual tours for businesses, real estate, and more. Better than Matterport.';
    case 'LED Printing':
      return 'LED printing services for businesses and organizations.';
    default:
      return '';
  }
};

export default Header;
